import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import { Form, message } from "antd";
import type { RuleRender } from "antd/lib/form";
import moment, { Moment } from "moment";
import { captureException } from "@sentry/nextjs";
import InfyButton from "../../../InfyButton/InfyButton";
import { fetchLearnerResponsesForAdmin, getLearnerResponseByFormId, searchLearnerCompanies, submitLearnerResponse } from "../../apis";
import { buildFieldValuesFromDraft, buildResponses } from "../../formUtils";
import getQuestionComponent from "../../getQuestionComponent";
import { INTERNSHIP_FIELDS, InternshipField, QUESTION_TYPES } from "../../constants";
import { resolveSubmitErrorMessage, V2FormData, V2Question, V2Response } from "../../helper";
import AutoCloseNotice from "../AutoCloseNotice/AutoCloseNotice";
import CompanyWebsiteField, { toCompanyWebsiteAnswer, usesCompanyWebsiteInput } from "./CompanyWebsiteField";
import DatePicker from "../DatePicker";
import FileUpload from "../FileUpload";
import JobFormCompleted from "../JobForm/JobFormCompleted";
import LocationField, { usesLocationInput } from "./LocationField";
import TenantLogo from "../TenantLogo/TenantLogo";
import useTenantLogo from "../TenantLogo/useTenantLogo";
import MasterListField, { MasterListValue } from "./MasterListField";
import OffCampusRequestSkeleton from "./OffCampusRequestSkeleton";
import StipendField, { toStipendAnswer, toStipendDisplayValue, usesStipendInput } from "./StipendField";
import { useMasterLists } from "./useMasterLists";
import styles from "./OffCampusRequestForm.module.scss";

interface OffCampusRequestFormProps {
  formData: V2FormData;
  formId: string;
  parameters: Record<string, string>;
}

const KNOWN_INTERNSHIP_FIELDS: string[] = Object.values(INTERNSHIP_FIELDS);

/** The internship field a question fills, or null when it has no marker or one this app does not know. */
const readInternshipField = (question: V2Question): InternshipField | null => {
  const value = (question.metadata as { internshipField?: unknown } | null | undefined)?.internshipField;
  return typeof value === "string" && KNOWN_INTERNSHIP_FIELDS.includes(value) ? (value as InternshipField) : null;
};

/**
 * Company is picked from the learner's own tenant list — the same companies their tenant posts jobs
 * against — searched server-side; Role is picked from the Airtable master list's `role` slug, the slug
 * the job form posts against, loaded once with Location and filtered in the browser. Both fields are
 * closed, so an answer always names an entry the list returned. The job form keeps searching the
 * global master list through `searchCompanies` / `searchRoles`, and keeps offering what the learner
 * typed; neither is touched from here.
 */
const PICKER_NOUNS: Partial<Record<InternshipField, string>> = {
  [INTERNSHIP_FIELDS.COMPANY]: "company",
  [INTERNSHIP_FIELDS.ROLE]: "role"
};

/** The seeded fields in layout order. A row of two sits side by side from the `sm` breakpoint up. */
const LAYOUT_ROWS: InternshipField[][] = [
  [INTERNSHIP_FIELDS.COMPANY, INTERNSHIP_FIELDS.ROLE],
  [INTERNSHIP_FIELDS.COMPANY_WEBSITE],
  [INTERNSHIP_FIELDS.LOCATION, INTERNSHIP_FIELDS.STIPEND],
  [INTERNSHIP_FIELDS.START_DATE, INTERNSHIP_FIELDS.END_DATE],
  [INTERNSHIP_FIELDS.MODE],
  [INTERNSHIP_FIELDS.IRF_DOCUMENT, INTERNSHIP_FIELDS.OFFER_LETTER]
];

/**
 * The end date may not fall before the start date. The picker already greys out the earlier days;
 * this catches a start date moved past an end date that was picked first.
 */
const endNotBeforeStart =
  (startQuestionId: string): RuleRender =>
  ({ getFieldValue }) => ({
    validator: (_, end) => {
      const start = getFieldValue(startQuestionId);
      return moment.isMoment(start) && moment.isMoment(end) && end.isBefore(start, "day")
        ? Promise.reject(new Error("The end date can't be before the start date"))
        : Promise.resolve();
    }
  });

/**
 * The submission payload. Every answer goes through the shared `buildResponses`. Company and role are
 * first flattened to their names, so the answer is the display name like any text question's, and the
 * picked entry's id rides in that response's `otherFields.masterListId` (the tenant company's `_id`,
 * the role's `Id`). Both fields are closed to their list, so a new submission always carries an id;
 * the `otherFields`-less branch below remains for a marked question that is not a picker at all (not
 * SHORT_TEXT), whose answer is plain text and which approval stores with a null id.
 * Location is a picker too but its list has no ids, so it is an ordinary text answer here.
 * The stipend is sent as its digits ("18000") even on a number question, which core-api reads as the
 * monthly amount. The company website is sent as typed (trimmed); core-api turns a bare domain into a URL.
 */
const toResponses = (
  questions: V2Question[],
  values: Record<string, unknown>,
  masterListIds: Set<string>,
  stipendId: string | null,
  websiteId: string | null
): V2Response[] => {
  const picked = (questionId: string) => (masterListIds.has(questionId) ? (values?.[questionId] as MasterListValue | undefined) : undefined);
  const flatValues: Record<string, unknown> = { ...values };
  masterListIds.forEach(questionId => {
    flatValues[questionId] = picked(questionId)?.name;
  });

  return buildResponses(questions, flatValues).map(response => {
    if (response.questionId === stipendId) return { ...response, answer: toStipendAnswer(values?.[stipendId]) };
    if (response.questionId === websiteId) return { ...response, answer: toCompanyWebsiteAnswer(values?.[websiteId]) };
    const masterListId = picked(response.questionId)?.masterListId;
    return masterListId ? { ...response, otherFields: { masterListId } } : response;
  });
};

/**
 * A saved submission as field values: the shared conversion, with company / role put back into the
 * picker's shape and the stipend as its amount when it is plainly one (see `toStipendDisplayValue`).
 */
const toFieldValues = (questions: V2Question[], responses: V2Response[], masterListIds: Set<string>, stipendId: string | null): Record<string, unknown> => {
  const values = buildFieldValuesFromDraft(questions, responses);
  responses.forEach(({ questionId, answer, otherFields }) => {
    if (questionId === stipendId) {
      if (answer !== null && answer !== undefined) values[questionId] = toStipendDisplayValue(answer);
      return;
    }
    if (!masterListIds.has(questionId) || typeof answer !== "string" || !answer) return;
    const masterListId = otherFields?.masterListId;
    values[questionId] = typeof masterListId === "string" && masterListId ? { name: answer, masterListId } : { name: answer };
  });
  return values;
};

/**
 * The page and its white card, shared by the form, the result screen and the admin's empty state. The tenant
 * logo, when there is one, sits above the card in the card's column, aligned with its left edge (the job form's
 * header).
 */
const Card: FunctionComponent<{ logo: string }> = ({ logo, children }) => (
  <div className="min-h-screen bg-neutral-2 px-4 py-6">
    {logo && (
      <div className="mx-auto flex w-full max-w-2xl flex-col">
        <TenantLogo src={logo} />
      </div>
    )}
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-5 rounded-2xl bg-white p-5 shadow-sm sm:p-8">{children}</div>
  </div>
);

/**
 * Dedicated layout for OFF_CAMPUS_REQUEST forms, selected on the form's type the same way GEO_CAPTURE
 * and job-form surveys are.
 *
 * Form Builder seeds these forms with questions marked by the internship field they fill
 * (`metadata.internshipField`), and approving a request copies their answers into the student's
 * internship details. They are found here by that marker, never by label or position, since admins
 * may reword and reorder them, and laid out as one block:
 *  - Company is picked from the learner's tenant list (`/v3/learner/companies`, searched server-side)
 *    and Role from the master list's `role` slug, and only from them: typing narrows the list but
 *    never becomes an answer of its own. The answer is the name; the picked entry's id goes in
 *    `otherFields.masterListId`.
 *  - Start and End date are checked as a range: the end may not fall before the start.
 *  - Stipend is an amount per month (digits only, "INR" … "/ month"), sent as its digits.
 *  - Company website (its own row under Company / Role) takes a URL or a bare domain, checked only when filled.
 *  - Location is picked from the master list's `city` slug — the other half of Role's one read — or
 *    typed: it stays open, unlike Company and Role, because its names are submitted as names, nothing
 *    downstream resolves them, and a student interning outside the list must still be able to submit.
 *    The answer is the place name alone and no `masterListId` is sent.
 *  - Role and Location come from one request for both slugs, held for the form's lifetime. When it
 *    fails, both fields say so and offer a retry; Role cannot be answered until it succeeds (it is
 *    closed), while Location keeps taking a typed place.
 *  - Mode, the IRF document and the offer letter render with the generic question components.
 * Every other question the admin added renders beneath, with the generic renderer. A form whose
 * questions carry no markers (an older one, or a core-api that does not send them) therefore renders
 * every question generically — never a blank form.
 *
 * Already-submitted and resubmission behave like the job form: the form type always allows
 * resubmission, so a returning learner gets a fresh form, and the result screen offers another.
 */
const OffCampusRequestForm: FunctionComponent<OffCampusRequestFormProps> = ({ formData, formId, parameters }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // The learner has a completed submission and the form does not take another.
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  // The existing-submission (or admin) read has finished. The skeleton shows until it has.
  const [checked, setChecked] = useState(false);
  // Admin viewing one student's response: their answers as field values (null when none was found).
  const [adminValues, setAdminValues] = useState<Record<string, unknown> | null>(null);
  // The tenant logo on every screen of this layout, as the job form shows it ("" = none).
  const logo = useTenantLogo();

  // `hideButtons` is set for both the builder preview and the admin "view a response" drawer; a
  // `submissionId` tells the two apart. Both are read-only.
  const { hideHeaders, hideButtons, submissionId } = parameters || {};
  const isAdminView = Boolean(submissionId);
  const isBuilderPreview = Boolean(hideButtons) && !isAdminView;
  const readOnly = Boolean(hideButtons) || isAdminView;
  const canResubmit = Boolean((formData?.settings as { allowResubmission?: boolean } | undefined)?.allowResubmission);

  const questions: V2Question[] = useMemo(() => [...(formData?.questions || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [formData]);

  // Each seeded field's question, by marker. If two questions carry the same marker the first in the
  // form's order is the field (approval reads it first too) and the other renders with the admin's own.
  const { seeded, extraQuestions } = useMemo(() => {
    const byField = new Map<InternshipField, V2Question>();
    const rest: V2Question[] = [];
    questions.forEach(question => {
      const field = readInternshipField(question);
      if (field && !byField.has(field)) byField.set(field, question);
      else rest.push(question);
    });
    return { seeded: byField, extraQuestions: rest };
  }, [questions]);

  // Company / role become pickers only on a short text question, the type Form Builder seeds; a
  // marked question of any other type renders as it was built.
  const masterListIds = useMemo(() => {
    const ids = new Set<string>();
    [INTERNSHIP_FIELDS.COMPANY, INTERNSHIP_FIELDS.ROLE].forEach(field => {
      const question = seeded.get(field);
      if (question?.questionType === QUESTION_TYPES.SHORT_TEXT) ids.add(question.id);
    });
    return ids;
  }, [seeded]);

  // The stipend takes the amount-per-month input on a text or number question, the types Form Builder
  // has seeded it as; a stipend of any other type renders as it was built.
  const stipendQuestion = seeded.get(INTERNSHIP_FIELDS.STIPEND);
  const stipendId = stipendQuestion && usesStipendInput(stipendQuestion) ? stipendQuestion.id : null;

  // The company website takes the URL input on a short text question, the type Form Builder seeds.
  const websiteQuestion = seeded.get(INTERNSHIP_FIELDS.COMPANY_WEBSITE);
  const websiteId = websiteQuestion && usesCompanyWebsiteInput(websiteQuestion) ? websiteQuestion.id : null;

  // Location takes the place picker on a short text question, the type Form Builder seeds.
  const locationQuestion = seeded.get(INTERNSHIP_FIELDS.LOCATION);
  const locationId = locationQuestion && usesLocationInput(locationQuestion) ? locationQuestion.id : null;

  // The one master-list read behind Role and Location — skipped entirely when this form has neither
  // as a picker, and in the read-only views, which show saved answers and load no lists.
  const roleQuestion = seeded.get(INTERNSHIP_FIELDS.ROLE);
  const roleId = roleQuestion && masterListIds.has(roleQuestion.id) ? roleQuestion.id : null;
  const masterLists = useMasterLists(!readOnly && (roleId !== null || locationId !== null), { requireRoles: roleId !== null });
  const roleOptions = useMemo(() => masterLists.roles.map(({ Id, Name }) => ({ label: Name, value: Id })), [masterLists.roles]);

  const startQuestion = seeded.get(INTERNSHIP_FIELDS.START_DATE);
  const endQuestion = seeded.get(INTERNSHIP_FIELDS.END_DATE);
  const isDateRange = startQuestion?.questionType === QUESTION_TYPES.DATE_PICKER && endQuestion?.questionType === QUESTION_TYPES.DATE_PICKER;
  const watchedStart = Form.useWatch(startQuestion?.id, form);
  const startDate: Moment | null = isDateRange && moment.isMoment(watchedStart) ? watchedStart : null;

  useEffect(() => {
    // Builder preview: nothing to load, the empty form renders.
    if (isBuilderPreview) {
      setChecked(true);
      return undefined;
    }
    let active = true;
    const load = async () => {
      try {
        if (isAdminView) {
          const res = await fetchLearnerResponsesForAdmin(formId, submissionId);
          const responses = (res?.data?.responses || []) as V2Response[];
          if (active) setAdminValues(responses.length > 0 ? toFieldValues(questions, responses, masterListIds, stipendId) : null);
        } else {
          // Like the job form: the result screen only when the learner has a completed submission and
          // the server does not explicitly say they can submit again.
          const res = await getLearnerResponseByFormId(formId);
          const existing = res?.data?.existingSubmission as { status?: string } | undefined;
          if (active && existing?.status === "COMPLETED" && res?.data?.canSubmit !== true) setAlreadySubmitted(true);
        }
      } catch {
        // Fails open: the backend still refuses a submission it does not allow.
      } finally {
        if (active) setChecked(true);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [formId, isBuilderPreview, isAdminView, submissionId]);

  const handleFinish = async (values: Record<string, unknown>) => {
    if (submitting || readOnly) return;
    setSubmitting(true);
    try {
      await submitLearnerResponse(formId, {
        responses: toResponses(questions, values, masterListIds, stipendId, websiteId),
        componentId: parameters?.componentId,
        courseId: parameters?.courseId || null,
        // Both spellings: core-api's link generator lowercases these query keys, other callers use camelCase.
        relatedModelName: parameters?.relatedModelName || parameters?.relatedModelname,
        relatedModelId: parameters?.relatedModelId || parameters?.relatedModelid
      });
      setSubmitted(true);
      window?.parent?.postMessage({ action: "formSubmitted" }, "*");
    } catch (error) {
      captureException(error);
      message.error(resolveSubmitErrorMessage(error));
      window?.parent?.postMessage({ action: "somethingWentWrong" }, "*");
    } finally {
      setSubmitting(false);
    }
  };

  // A blank form for another response, without leaving the page.
  const handleSubmitAnother = () => {
    form.resetFields();
    setSubmitted(false);
    if (typeof window !== "undefined") window.scrollTo({ top: 0 });
  };

  if (!checked) return <OffCampusRequestSkeleton />;

  if (submitted || alreadySubmitted) {
    const returning = alreadySubmitted && !submitted;
    return (
      <Card logo={logo}>
        <JobFormCompleted
          title={returning ? "Already Submitted" : undefined}
          message={returning ? "You have already sent this request. It accepts one response only." : "Your internship data has been sent for review."}
          action={
            submitted && canResubmit ? (
              <InfyButton type="ghost" bicolor="primary" onClick={handleSubmitAnother}>
                Submit another response
              </InfyButton>
            ) : undefined
          }
        />
        {/* Only after a submit that just succeeded, never for a returning learner. "Submit another
            response" unmounts it with the result screen, which stops the countdown. */}
        {submitted && <AutoCloseNotice />}
      </Card>
    );
  }

  const title = parameters?.formtitle || formData?.title;
  const header =
    !hideHeaders && (title || formData?.description) ? (
      <div className="flex flex-col gap-1">
        {title && <h1 className="m-0 text-xl font-semibold text-neutral-13">{title}</h1>}
        {formData?.description && <p className="m-0 text-sm text-neutral-9">{formData.description}</p>}
      </div>
    ) : null;

  if (isAdminView && !adminValues) {
    return (
      <Card logo={logo}>
        {header}
        <p className="m-0 text-sm text-neutral-8">No response found.</p>
      </Card>
    );
  }

  const renderQuestion = (question: V2Question) =>
    getQuestionComponent({ question, disabled: readOnly, formType: formData?.type, legacyId: formData?.metadata?.legacyId });

  const renderSeeded = (field: InternshipField, storedQuestion: V2Question) => {
    // The offer letter is always optional here: a learner may not have one yet, and forms saved while it was
    // required still carry `isRequired: true`. A copy, so the stored question is untouched.
    const question = field === INTERNSHIP_FIELDS.OFFER_LETTER && storedQuestion.isRequired ? { ...storedQuestion, isRequired: false } : storedQuestion;
    const noun = PICKER_NOUNS[field];
    if (noun && masterListIds.has(question.id)) {
      const source =
        field === INTERNSHIP_FIELDS.COMPANY
          ? ({ kind: "search", search: searchLearnerCompanies } as const)
          : ({
              kind: "list",
              options: roleOptions,
              loading: masterLists.status === "loading",
              failed: masterLists.status === "failed",
              onRetry: masterLists.retry
            } as const);
      return <MasterListField question={question} source={source} noun={noun} disabled={readOnly} />;
    }
    if (field === INTERNSHIP_FIELDS.STIPEND && question.id === stipendId) {
      return <StipendField question={question} disabled={readOnly} />;
    }
    if (field === INTERNSHIP_FIELDS.LOCATION && question.id === locationId) {
      return (
        <LocationField
          question={question}
          locations={masterLists.locations}
          loading={masterLists.status === "loading"}
          failed={masterLists.status === "failed"}
          onRetry={masterLists.retry}
          disabled={readOnly}
        />
      );
    }
    if (field === INTERNSHIP_FIELDS.COMPANY_WEBSITE && question.id === websiteId) {
      return <CompanyWebsiteField question={question} disabled={readOnly} />;
    }
    if (field === INTERNSHIP_FIELDS.END_DATE && isDateRange && startQuestion) {
      return (
        <DatePicker
          question={question}
          disabled={readOnly}
          dependencies={[startQuestion.id]}
          disabledDate={current => startDate !== null && current.isBefore(startDate, "day")}
          extraRules={[endNotBeforeStart(startQuestion.id)]}
        />
      );
    }
    // The IRF document and offer letter are PDF only here, whatever file types the question was seeded with.
    if (question.questionType === QUESTION_TYPES.FILE_UPLOAD && (field === INTERNSHIP_FIELDS.IRF_DOCUMENT || field === INTERNSHIP_FIELDS.OFFER_LETTER)) {
      return <FileUpload question={question} disabled={readOnly} fileTypes={["pdf"]} />;
    }
    return renderQuestion(question);
  };

  const seededRows = LAYOUT_ROWS.map(row =>
    row
      .map(field => ({ field, question: seeded.get(field) }))
      .filter((cell): cell is { field: InternshipField; question: V2Question } => Boolean(cell.question))
  ).filter(row => row.length > 0);

  return (
    <Card logo={logo}>
      {header}

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={adminValues || undefined}
        onFinish={handleFinish}
        scrollToFirstError
        className={`flex flex-col ${styles.offCampusFields}`}
      >
        {seededRows.map(row => (
          <div key={row[0].question.id} className={row.length > 1 ? "grid grid-cols-1 gap-x-4 sm:grid-cols-2" : undefined}>
            {row.map(({ field, question }) => (
              <div key={question.id} className="min-w-0">
                {renderSeeded(field, question)}
              </div>
            ))}
          </div>
        ))}

        {seededRows.length > 0 && extraQuestions.length > 0 && <div className="mb-6 h-px w-full bg-neutral-4" />}

        {extraQuestions.map(question => (
          <div key={question.id}>{renderQuestion(question)}</div>
        ))}

        {!readOnly && (
          <InfyButton type="primary" bicolor="primary" htmlType="submit" className="mt-2 w-full" loading={submitting} disabled={submitting}>
            Submit
          </InfyButton>
        )}
      </Form>
    </Card>
  );
};

export default OffCampusRequestForm;
